import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { 
  LayoutDashboard, 
  Image, 
  SlidersHorizontal, 
  Newspaper, 
  HelpCircle,
  Clock,
  DollarSign,
  Ticket,
  Tag,
  Languages,
  Globe,
  Users,
  UserCog,
  LogOut,
  Menu,
  X,
  ChevronDown,
  FileText,
  BookOpen,
  CalendarDays,
  Mail,
  Mountain,
  ArrowUpDown,
  GraduationCap,
  Phone,
  Gift,
  Award,
  Cookie,
  Power,
  Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useState } from 'react';
import Logo from '../../components/Logo';

const AdminSidebar = ({ isOpen, onClose }) => {
  const { admin, logout, hasPermission } = useAdminAuth();
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { 
      id: 'dashboard',
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      path: '/admin',
      permission: null // Available to all
    },
    { 
      icon: BookOpen, 
      label: 'Blog', 
      path: '/admin/blog',
      permission: 'news'
    },
    {
      icon: CalendarDays,
      label: 'Wydarzenia',
      path: '/admin/events',
      permission: 'news'
    },
    {
      id: 'content',
      icon: Image,
      label: 'Treści',
      permission: 'hero',
      children: [
        { icon: Image, label: 'Hero', path: '/admin/hero', permission: 'hero' },
        { icon: SlidersHorizontal, label: 'Slider', path: '/admin/slider', permission: 'slider' },
        { icon: HelpCircle, label: 'FAQ', path: '/admin/faq', permission: 'faq' },
        { icon: Mountain, label: 'Park', path: '/admin/park', permission: 'hero' },
        { icon: GraduationCap, label: 'Zajęcia', path: '/admin/classes', permission: 'instructors' },
      ]
    },
    {
      id: 'business',
      icon: DollarSign,
      label: 'Biznes',
      permission: 'passes_view',
      children: [
        { icon: Clock, label: 'Godziny otwarcia', path: '/admin/hours', permission: 'hours' },
        { icon: DollarSign, label: 'Cennik', path: '/admin/pricing', permission: 'pricing' },
        { icon: Ticket, label: 'Karnety', path: '/admin/passes', permission: 'passes_view' },
        { icon: Tag, label: 'Rabaty', path: '/admin/discounts', permission: 'discounts_view' },
      ]
    },
    {
      id: 'translations',
      icon: Languages,
      label: 'Tłumaczenia',
      permission: 'translations',
      children: [
        { icon: Languages, label: 'Wszystkie teksty', path: '/admin/translations', permission: 'translations' },
        { icon: Globe, label: 'Języki', path: '/admin/languages', permission: 'translations' },
        { icon: Mail, label: 'Szablony emaili', path: '/admin/email-templates', permission: 'notifications' },
        { icon: Tag, label: 'FitNet (karnety, zajęcia)', path: '/admin/fitnet-translations', permission: 'translations' },
        { icon: ArrowUpDown, label: 'Kolejność wyświetlania', path: '/admin/sort-order', permission: 'translations' },
      ]
    },
    {
      id: 'settings',
      icon: Phone,
      label: 'Ustawienia',
      permission: 'settings',
      children: [
        { icon: Power, label: 'Dostępność usług', path: '/admin/service-availability', permission: 'settings' },
        { icon: Phone, label: 'Prefiksy telefoniczne', path: '/admin/phone-prefixes', permission: 'settings' },
        { icon: Users, label: 'Klienci', path: '/admin/customers', permission: 'users_view' },
        { icon: Gift, label: 'Emaile urodzinowe', path: '/admin/birthday-emails', permission: 'notifications' },
        { icon: Award, label: 'Program lojalnościowy', path: '/admin/loyalty', permission: 'settings' },
        { icon: Cookie, label: 'GDPR / Cookie Consent', path: '/admin/cookie-consent', permission: 'settings' },
      ]
    },
    { 
      icon: Users, 
      label: 'Pracownicy', 
      path: '/admin/instructors',
      permission: 'instructors'
    },
    { 
      icon: FileText, 
      label: 'Dokumenty', 
      path: '/admin/documents',
      permission: 'documents'
    },
    { 
      icon: UserCog, 
      label: 'Użytkownicy Admin', 
      path: '/admin/users',
      permission: 'admin_users'
    },
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const renderMenuItem = (item, index) => {
    // Check permission
    if (item.permission && !hasPermission(item.permission)) {
      return null;
    }

    // If has children (dropdown)
    if (item.children) {
      const hasAnyChildPermission = item.children.some(
        child => !child.permission || hasPermission(child.permission)
      );
      if (!hasAnyChildPermission) return null;

      const isExpanded = expandedSection === item.id;

      return (
        <div key={item.id} className="space-y-1">
          <button
            onClick={() => toggleSection(item.id)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
          
          {isExpanded && (
            <div className="ml-4 space-y-1 border-l border-white/10 pl-3">
              {item.children.map((child, idx) => {
                if (child.permission && !hasPermission(child.permission)) {
                  return null;
                }
                return (
                  <NavLink
                    key={idx}
                    to={child.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-purple-600/20 text-purple-400'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`
                    }
                  >
                    <child.icon className="w-4 h-4" />
                    <span className="text-sm">{child.label}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular menu item
    return (
      <NavLink
        key={index}
        to={item.path}
        end={item.path === '/admin'}
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
            isActive
              ? 'bg-purple-600/20 text-purple-400'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`
        }
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-gray-900 border-r border-white/10 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-auto" variant="light" />
            <p className="text-xs text-gray-500">Admin</p>
          </div>
          <button onClick={onClose} className="lg:hidden text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center">
              <span className="text-purple-400 font-semibold">
                {admin?.name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{admin?.name}</p>
              <p className="text-gray-500 text-xs truncate">{admin?.role}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-200px)]">
          {menuItems.map((item, index) => renderMenuItem(item, index))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Wyloguj
          </Button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
